/**
 * MapEditor - マップエディタメインコンテナ
 *
 * 機能:
 * - ヘクスグリッド表示と編集
 * - 地形/都市/町の配置
 * - マップ拡張（ヘクス追加）
 * - マップのインポート/エクスポート/保存/ロード
 */

import { useState, useCallback } from 'react';
import { HexGrid, type EditorHex, type EditorTerrainType, type EditorCity, type EditorTown, type EditorFerryLink, type EditorAdjacentLink, type TemporaryTrackPlacement } from './HexGrid';
import { TerrainPalette, type EditorTool } from './TerrainPalette';
import { CityTownEditor } from './CityTownEditor';
import { ExportPanel, type MapInfo } from './ExportPanel';
import { PreLaidTrackPanel } from './PreLaidTrackPanel';
import { BASE_PATH } from '../../config';

// ============================================================================
// 型定義
// ============================================================================

interface MapEditorProps {
  initialHexes?: EditorHex[];
  initialMapInfo?: MapInfo;
}

interface EditingHex {
  hex: EditorHex;
  mode: 'city' | 'town';
}

/**
 * マップデータのJSON形式（ロード用）
 */
interface MapDataJson {
  map_info?: {
    name?: string;
    designer?: string;
    players?: number[];
  };
  cities?: Array<{
    name: string;
    color: string | string[];  // マップデータは配列形式 ["yellow"] だが、単体文字列もサポート
    coordinates: { q: number; r: number };
    dice_color?: string | string[];  // 単体または配列
    dice_numbers?: number[];
  }>;
  towns?: Array<{
    name: string;
    coordinates: { q: number; r: number };
    urbanizable: boolean;
    terrain?: string;
  }>;
  terrain?: {
    mountains?: Array<{ q: number; r: number }>;
    rivers?: Array<{ q: number; r: number }>;
    plains?: Array<{ q: number; r: number }>;
  };
  // リンク
  ferry_links?: Array<{
    from: { q: number; r: number };
    to: { q: number; r: number };
    cost: number;
  }>;
  adjacent_links?: Array<{
    from: { q: number; r: number };
    to: { q: number; r: number };
    cost: number;
  }>;
  // 壁
  walls?: Array<{
    coordinates: { q: number; r: number };
    blocked_edges: number[];
  }>;
  // 敷設済み線路
  pre_laid_tracks?: Array<{
    coordinates: { q: number; r: number };
    tile_type: string;
    rotation: number;
  }>;
}

// ============================================================================
// ヘルパー関数
// ============================================================================

/**
 * デフォルトのヘクスグリッドを生成
 */
function generateDefaultGrid(rows: number, cols: number): EditorHex[] {
  const hexes: EditorHex[] = [];
  for (let r = 0; r < rows; r++) {
    for (let q = 0; q < cols; q++) {
      hexes.push({
        q,
        r,
        terrain: 'plains',
      });
    }
  }
  return hexes;
}

/**
 * 6方向の隣接座標オフセット（Pointy-top）
 */
const NEIGHBOR_OFFSETS = [
  { q: +1, r: -1 }, // NE
  { q: +1, r: 0 },  // E
  { q: 0, r: +1 },  // SE
  { q: -1, r: +1 }, // SW
  { q: -1, r: 0 },  // W
  { q: 0, r: -1 },  // NW
];

/**
 * 隣接するヘクス座標を取得
 */
function getNeighborCoords(q: number, r: number): { q: number; r: number }[] {
  return NEIGHBOR_OFFSETS.map(offset => ({
    q: q + offset.q,
    r: r + offset.r,
  }));
}

/**
 * 2つのヘクスが隣接しているかを判定
 */
function areHexesAdjacent(a: { q: number; r: number }, b: { q: number; r: number }): boolean {
  const neighbors = getNeighborCoords(a.q, a.r);
  return neighbors.some(n => n.q === b.q && n.r === b.r);
}

/**
 * リンクIDを生成
 */
function generateLinkId(): string {
  return `link_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================================================
// MapEditor コンポーネント
// ============================================================================

export function MapEditor({
  initialHexes,
  initialMapInfo,
}: MapEditorProps) {
  // 状態管理
  const [hexes, setHexes] = useState<EditorHex[]>(
    initialHexes || generateDefaultGrid(8, 12)
  );
  const [mapInfo, setMapInfo] = useState<MapInfo>(
    initialMapInfo || {
      name: 'New Map',
      designer: '',
      players: [1, 2, 3, 4],
    }
  );

  const [selectedTool, setSelectedTool] = useState<EditorTool>('terrain');
  const [selectedTerrain, setSelectedTerrain] = useState<EditorTerrainType>('plains');
  const [selectedHex, setSelectedHex] = useState<{ q: number; r: number } | null>(null);
  const [editingHex, setEditingHex] = useState<EditingHex | null>(null);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('landscape');

  // リンク
  const [ferryLinks, setFerryLinks] = useState<EditorFerryLink[]>([]);
  const [adjacentLinks, setAdjacentLinks] = useState<EditorAdjacentLink[]>([]);
  const [linkStartHex, setLinkStartHex] = useState<{ q: number; r: number } | null>(null);

  // グリッドサイズ変更用
  const [gridRows, setGridRows] = useState(8);
  const [gridCols, setGridCols] = useState(12);

  // ズーム機能
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 3.0;
  const ZOOM_STEP = 0.25;

  // 保存/ロード用
  const [saveStatus, setSaveStatus] = useState<string>('');

  // 敷設済み線路編集用
  const [preLaidTileType, setPreLaidTileType] = useState<string | null>(null);
  const [preLaidRotation, setPreLaidRotation] = useState<number>(0);
  const [temporaryTrackPlacement, setTemporaryTrackPlacement] = useState<TemporaryTrackPlacement | null>(null);

  /**
   * ヘクスを更新
   */
  const updateHex = useCallback((q: number, r: number, updates: Partial<EditorHex>) => {
    setHexes((prev) =>
      prev.map((hex) =>
        hex.q === q && hex.r === r ? { ...hex, ...updates } : hex
      )
    );
  }, []);

  /**
   * ヘクスを追加
   */
  const addHex = useCallback((q: number, r: number, terrain: EditorTerrainType) => {
    setHexes((prev) => {
      // 既に存在する場合は追加しない
      if (prev.some(h => h.q === q && h.r === r)) {
        return prev;
      }
      return [...prev, { q, r, terrain }];
    });
  }, []);

  /**
   * ヘクスクリック時の処理
   */
  const handleHexClick = useCallback((hex: EditorHex) => {
    setSelectedHex({ q: hex.q, r: hex.r });

    switch (selectedTool) {
      case 'terrain':
        updateHex(hex.q, hex.r, { terrain: selectedTerrain });
        break;
      case 'city':
        setEditingHex({ hex, mode: 'city' });
        break;
      case 'town':
        setEditingHex({ hex, mode: 'town' });
        break;
      case 'eraser':
        updateHex(hex.q, hex.r, {
          terrain: 'plains',
          city: undefined,
          town: undefined,
        });
        break;
      case 'expand':
        // 隣接するヘクスを追加
        const neighbors = getNeighborCoords(hex.q, hex.r);
        neighbors.forEach(({ q, r }) => {
          addHex(q, r, selectedTerrain);
        });
        break;
      case 'ferryLink':
        // フェリーリンクの作成（離れたヘクス間）
        if (linkStartHex) {
          if (linkStartHex.q === hex.q && linkStartHex.r === hex.r) {
            // 同じヘクスをクリックしたらキャンセル
            setLinkStartHex(null);
          } else {
            // コストを入力してリンクを作成
            const costStr = prompt('Enter ferry link cost:', '2');
            if (costStr !== null) {
              const cost = parseInt(costStr) || 2;
              const newLink: EditorFerryLink = {
                id: generateLinkId(),
                from: linkStartHex,
                to: { q: hex.q, r: hex.r },
                cost,
              };
              setFerryLinks(prev => [...prev, newLink]);
            }
            setLinkStartHex(null);
          }
        } else {
          setLinkStartHex({ q: hex.q, r: hex.r });
        }
        break;
      case 'adjacentLink':
        // 隣接リンクの作成（隣接ヘクス間）
        if (linkStartHex) {
          if (linkStartHex.q === hex.q && linkStartHex.r === hex.r) {
            // 同じヘクスをクリックしたらキャンセル
            setLinkStartHex(null);
          } else if (areHexesAdjacent(linkStartHex, hex)) {
            // コストを入力してリンクを作成
            const costStr = prompt('Enter adjacent link cost:', '1');
            if (costStr !== null) {
              const cost = parseInt(costStr) || 1;
              const newLink: EditorAdjacentLink = {
                id: generateLinkId(),
                from: linkStartHex,
                to: { q: hex.q, r: hex.r },
                cost,
              };
              setAdjacentLinks(prev => [...prev, newLink]);
            }
            setLinkStartHex(null);
          } else {
            alert('Adjacent Link requires two adjacent hexes. Please select an adjacent hex.');
          }
        } else {
          setLinkStartHex({ q: hex.q, r: hex.r });
        }
        break;
      case 'wall':
        // 壁の編集（ポップアップで方向を選択）
        {
          const currentWalls = hex.blockedEdges || [];
          const directions = ['0 (NE)', '1 (E)', '2 (SE)', '3 (SW)', '4 (W)', '5 (NW)'];
          const wallStr = prompt(
            `Toggle walls for (${hex.q}, ${hex.r})\n` +
            `Current walls: [${currentWalls.join(', ')}]\n` +
            `Enter comma-separated directions to toggle (0-5):\n` +
            directions.join(', '),
            currentWalls.join(',')
          );
          if (wallStr !== null) {
            const newWalls = wallStr
              .split(',')
              .map(s => parseInt(s.trim()))
              .filter(n => !isNaN(n) && n >= 0 && n <= 5);
            updateHex(hex.q, hex.r, { blockedEdges: newWalls.length > 0 ? newWalls : undefined });
          }
        }
        break;
      case 'preLaidTrack':
        // 敷設済み線路の仮配置/回転/削除（Ph.4 スタイル）
        if (hex.preLaidTrack) {
          // 既に配置済み → 削除
          updateHex(hex.q, hex.r, { preLaidTrack: undefined });
        } else if (temporaryTrackPlacement && temporaryTrackPlacement.q === hex.q && temporaryTrackPlacement.r === hex.r) {
          // 仮配置中のヘクスをクリック → 回転
          setTemporaryTrackPlacement({
            ...temporaryTrackPlacement,
            rotation: (temporaryTrackPlacement.rotation + 1) % 6,
          });
        } else if (preLaidTileType) {
          // タイルが選択されている → 仮配置（別のヘクスに移動）
          setTemporaryTrackPlacement({
            q: hex.q,
            r: hex.r,
            tileType: preLaidTileType,
            rotation: preLaidRotation,
          });
        }
        break;
    }
  }, [selectedTool, selectedTerrain, updateHex, addHex, linkStartHex, preLaidTileType, preLaidRotation, temporaryTrackPlacement]);

  /**
   * ヘクス右クリック時の処理（編集ダイアログを開く）
   */
  const handleHexRightClick = useCallback((hex: EditorHex) => {
    setSelectedHex({ q: hex.q, r: hex.r });
    if (hex.city) {
      setEditingHex({ hex, mode: 'city' });
    } else if (hex.town) {
      setEditingHex({ hex, mode: 'town' });
    }
  }, []);

  /**
   * 都市/町の保存
   */
  const handleSaveCityTown = useCallback((data: EditorCity | EditorTown) => {
    if (!editingHex) return;

    if (editingHex.mode === 'city') {
      updateHex(editingHex.hex.q, editingHex.hex.r, {
        city: data as EditorCity,
        town: undefined,
      });
    } else {
      updateHex(editingHex.hex.q, editingHex.hex.r, {
        town: data as EditorTown,
        city: undefined,
      });
    }
    setEditingHex(null);
  }, [editingHex, updateHex]);

  /**
   * 都市/町の削除
   */
  const handleDeleteCityTown = useCallback(() => {
    if (!editingHex) return;

    updateHex(editingHex.hex.q, editingHex.hex.r, {
      city: undefined,
      town: undefined,
    });
    setEditingHex(null);
  }, [editingHex, updateHex]);

  /**
   * 敷設済み線路の仮配置を確定
   */
  const handleConfirmPreLaidTrack = useCallback(() => {
    if (!temporaryTrackPlacement) return;

    updateHex(temporaryTrackPlacement.q, temporaryTrackPlacement.r, {
      preLaidTrack: {
        tileType: temporaryTrackPlacement.tileType,
        rotation: (temporaryTrackPlacement.rotation % 6) as 0 | 1 | 2 | 3 | 4 | 5,
      }
    });
    setTemporaryTrackPlacement(null);
  }, [temporaryTrackPlacement, updateHex]);

  /**
   * 敷設済み線路の仮配置をキャンセル
   */
  const handleCancelPreLaidTrack = useCallback(() => {
    setTemporaryTrackPlacement(null);
  }, []);

  /**
   * ズームイン
   */
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  /**
   * ズームアウト
   */
  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  /**
   * グリッドサイズ変更
   */
  const handleResizeGrid = useCallback(() => {
    const newHexes: EditorHex[] = [];
    for (let r = 0; r < gridRows; r++) {
      for (let q = 0; q < gridCols; q++) {
        const existing = hexes.find((h) => h.q === q && h.r === r);
        if (existing) {
          newHexes.push(existing);
        } else {
          newHexes.push({ q, r, terrain: 'plains' });
        }
      }
    }
    setHexes(newHexes);
  }, [hexes, gridRows, gridCols]);

  /**
   * グリッドをクリア
   */
  const handleClearGrid = useCallback(() => {
    if (confirm('Are you sure you want to clear the entire grid?')) {
      setHexes(generateDefaultGrid(gridRows, gridCols));
    }
  }, [gridRows, gridCols]);

  /**
   * マップをdata/map/に保存
   */
  const handleSaveMap = useCallback(async () => {
    if (!mapInfo.name || mapInfo.name === 'New Map') {
      alert('Please enter a map name first.');
      return;
    }

    const mapData = convertToMapSchema(hexes, mapInfo, ferryLinks, adjacentLinks);
    const dirName = mapInfo.name.toLowerCase().replace(/\s+/g, '_');

    try {
      const response = await fetch(`${BASE_PATH}api/maps/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dirName,
          mapData,
        }),
      });

      if (response.ok) {
        setSaveStatus(`Saved to data/map/${dirName}/`);
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        // ファイルAPIが使えない場合はダウンロード
        downloadAsJson(mapData, dirName);
      }
    } catch {
      // APIが使えない場合はダウンロード
      downloadAsJson(mapData, dirName);
    }
  }, [hexes, mapInfo, ferryLinks, adjacentLinks]);

  /**
   * JSONとしてダウンロード
   */
  const downloadAsJson = (data: object, name: string) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}_map_data.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSaveStatus('Downloaded as JSON file');
    setTimeout(() => setSaveStatus(''), 3000);
  };

  /**
   * ファイルからロード
   */
  const handleFileLoad = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const mapData = JSON.parse(e.target?.result as string);
        loadFromMapData(mapData);
      } catch {
        alert('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  }, []);

  /**
   * マップデータから状態を復元
   */
  const loadFromMapData = (mapData: MapDataJson) => {
    const newHexes: EditorHex[] = [];

    // 都市
    if (mapData.cities) {
      mapData.cities.forEach((city) => {
        // color は配列形式 ["yellow"] または単体文字列 "yellow" の両方をサポート
        const colors = Array.isArray(city.color)
          ? city.color as EditorCity['colors']
          : [city.color] as EditorCity['colors'];
        // dice_numbers は空配列も許容（undefined の場合のみデフォルト値）
        const diceNumbers = city.dice_numbers !== undefined ? city.dice_numbers : [];
        // dice_color は配列形式または単体文字列の両方をサポート
        let diceColors: ('white' | 'black')[];
        if (Array.isArray(city.dice_color)) {
          diceColors = city.dice_color as ('white' | 'black')[];
        } else if (city.dice_color === 'black') {
          diceColors = ['black'];
        } else {
          diceColors = ['white'];
        }
        newHexes.push({
          q: city.coordinates.q,
          r: city.coordinates.r,
          terrain: 'plains',
          city: {
            name: city.name,
            colors,
            diceNumbers,
            diceColors,
          },
        });
      });
    }

    // 町
    if (mapData.towns) {
      mapData.towns.forEach((town) => {
        newHexes.push({
          q: town.coordinates.q,
          r: town.coordinates.r,
          terrain: (town.terrain === 'plain' ? 'plains' : town.terrain || 'plains') as EditorTerrainType,
          town: {
            name: town.name,
            urbanizable: town.urbanizable,
          },
        });
      });
    }

    // 地形
    if (mapData.terrain) {
      if (mapData.terrain.mountains) {
        mapData.terrain.mountains.forEach((coord) => {
          if (!newHexes.some(h => h.q === coord.q && h.r === coord.r)) {
            newHexes.push({ q: coord.q, r: coord.r, terrain: 'mountains' });
          }
        });
      }
      if (mapData.terrain.rivers) {
        mapData.terrain.rivers.forEach((coord) => {
          if (!newHexes.some(h => h.q === coord.q && h.r === coord.r)) {
            newHexes.push({ q: coord.q, r: coord.r, terrain: 'river' });
          }
        });
      }
      if (mapData.terrain.plains) {
        mapData.terrain.plains.forEach((coord) => {
          if (!newHexes.some(h => h.q === coord.q && h.r === coord.r)) {
            newHexes.push({ q: coord.q, r: coord.r, terrain: 'plains' });
          }
        });
      }
    }

    setHexes(newHexes);
    if (mapData.map_info) {
      setMapInfo({
        name: mapData.map_info.name || 'Loaded Map',
        designer: mapData.map_info.designer || '',
        players: mapData.map_info.players || [1, 2, 3, 4],
      });
    }

    // フェリーリンクをロード
    if (mapData.ferry_links) {
      const loadedFerryLinks: EditorFerryLink[] = mapData.ferry_links.map(link => ({
        id: generateLinkId(),
        from: link.from,
        to: link.to,
        cost: link.cost,
      }));
      setFerryLinks(loadedFerryLinks);
    } else {
      setFerryLinks([]);
    }

    // 隣接リンクをロード
    if (mapData.adjacent_links) {
      const loadedAdjacentLinks: EditorAdjacentLink[] = mapData.adjacent_links.map(link => ({
        id: generateLinkId(),
        from: link.from,
        to: link.to,
        cost: link.cost,
      }));
      setAdjacentLinks(loadedAdjacentLinks);
    } else {
      setAdjacentLinks([]);
    }

    // 壁をロード（既存ヘクスに追加）
    if (mapData.walls) {
      mapData.walls.forEach((wall) => {
        const hex = newHexes.find(h => h.q === wall.coordinates.q && h.r === wall.coordinates.r);
        if (hex) {
          hex.blockedEdges = wall.blocked_edges;
        } else {
          // ヘクスが存在しない場合は新規作成
          newHexes.push({
            q: wall.coordinates.q,
            r: wall.coordinates.r,
            terrain: 'plains',
            blockedEdges: wall.blocked_edges,
          });
        }
      });
      // 状態を更新（壁追加後）
      setHexes([...newHexes]);
    }

    // 敷設済み線路をロード（既存ヘクスに追加）
    if (mapData.pre_laid_tracks) {
      mapData.pre_laid_tracks.forEach((track) => {
        const hex = newHexes.find(h => h.q === track.coordinates.q && h.r === track.coordinates.r);
        if (hex) {
          hex.preLaidTrack = {
            tileType: track.tile_type,
            rotation: track.rotation as 0 | 1 | 2 | 3 | 4 | 5,
          };
        } else {
          // ヘクスが存在しない場合は新規作成
          newHexes.push({
            q: track.coordinates.q,
            r: track.coordinates.r,
            terrain: 'plains',
            preLaidTrack: {
              tileType: track.tile_type,
              rotation: track.rotation as 0 | 1 | 2 | 3 | 4 | 5,
            },
          });
        }
      });
      // 状態を更新（敷設済み線路追加後）
      setHexes([...newHexes]);
    }
  };

  return (
    <div className="flex h-full bg-background">
      {/* 左サイドパネル */}
      <div className="w-72 flex-shrink-0 border-r border-border overflow-y-auto p-4 space-y-4">
        {/* マップ名入力 */}
        <div className="p-4 bg-card border border-border rounded-lg">
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground">Map Name</h3>
          <input
            type="text"
            value={mapInfo.name}
            onChange={(e) => setMapInfo({ ...mapInfo, name: e.target.value })}
            placeholder="Enter map name"
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
          />
        </div>

        {/* ツールパレット */}
        <TerrainPalette
          selectedTool={selectedTool}
          selectedTerrain={selectedTerrain}
          onToolChange={(tool) => {
            setSelectedTool(tool);
            setLinkStartHex(null); // ツール変更時にリンク選択をリセット
          }}
          onTerrainChange={setSelectedTerrain}
        />

        {/* 敷設済み線路パネル（preLaidTrack ツール選択時のみ） */}
        {selectedTool === 'preLaidTrack' && (
          <PreLaidTrackPanel
            selectedTileType={preLaidTileType}
            selectedRotation={temporaryTrackPlacement?.rotation ?? preLaidRotation}
            onSelectTile={(tileType) => {
              setPreLaidTileType(tileType);
              // タイル変更時は仮配置も更新（配置済みの場合）
              if (temporaryTrackPlacement && tileType) {
                setTemporaryTrackPlacement({
                  ...temporaryTrackPlacement,
                  tileType,
                });
              }
            }}
            onRotate={() => {
              if (temporaryTrackPlacement) {
                // 仮配置中は仮配置の回転を更新
                setTemporaryTrackPlacement({
                  ...temporaryTrackPlacement,
                  rotation: (temporaryTrackPlacement.rotation + 1) % 6,
                });
              } else {
                setPreLaidRotation((prev) => (prev + 1) % 6);
              }
            }}
            onClear={() => {
              setPreLaidTileType(null);
              setPreLaidRotation(0);
              setTemporaryTrackPlacement(null);
            }}
            temporaryPlacement={temporaryTrackPlacement}
            onConfirm={handleConfirmPreLaidTrack}
            onCancel={handleCancelPreLaidTrack}
          />
        )}

        {/* グリッドサイズ */}
        <div className="p-4 bg-card border border-border rounded-lg">
          <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Grid Size</h3>
          <div className="flex gap-2 mb-3">
            <div className="flex-1">
              <label className="block text-xs mb-1">Rows</label>
              <input
                type="number"
                min={1}
                max={20}
                value={gridRows}
                onChange={(e) => setGridRows(parseInt(e.target.value) || 1)}
                className="w-full px-2 py-1 text-sm border border-border rounded bg-background"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs mb-1">Cols</label>
              <input
                type="number"
                min={1}
                max={20}
                value={gridCols}
                onChange={(e) => setGridCols(parseInt(e.target.value) || 1)}
                className="w-full px-2 py-1 text-sm border border-border rounded bg-background"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              className="flex-1 px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
              onClick={handleResizeGrid}
            >
              Resize
            </button>
            <button
              className="flex-1 px-3 py-1.5 text-xs bg-destructive text-destructive-foreground rounded hover:bg-destructive/90"
              onClick={handleClearGrid}
            >
              Clear
            </button>
          </div>
        </div>

        {/* 保存/ロード */}
        <div className="p-4 bg-card border border-border rounded-lg">
          <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Save / Load</h3>
          <div className="space-y-2">
            <button
              className="w-full px-3 py-2 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
              onClick={handleSaveMap}
            >
              Save Map
            </button>
            <label className="block">
              <span className="block w-full px-3 py-2 text-xs bg-muted hover:bg-muted/80 rounded cursor-pointer text-center">
                Load from File
              </span>
              <input
                type="file"
                accept=".json"
                onChange={handleFileLoad}
                className="hidden"
              />
            </label>
            {saveStatus && (
              <div className="text-xs text-green-600 text-center">{saveStatus}</div>
            )}
          </div>
        </div>

        {/* 表示設定 */}
        <div className="p-4 bg-card border border-border rounded-lg">
          <h3 className="text-sm font-semibold mb-3 text-muted-foreground">View Settings</h3>
          <button
            className="w-full px-3 py-2 text-sm bg-muted hover:bg-muted/80 rounded"
            onClick={() => setOrientation(orientation === 'portrait' ? 'landscape' : 'portrait')}
          >
            Orientation: {orientation}
          </button>
        </div>

        {/* リンク管理 */}
        {(ferryLinks.length > 0 || adjacentLinks.length > 0) && (
          <div className="p-4 bg-card border border-border rounded-lg">
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Links</h3>
            {ferryLinks.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs font-medium mb-1 text-primary">Ferry Links ({ferryLinks.length})</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {ferryLinks.map((link) => (
                    <div key={link.id} className="flex items-center justify-between text-xs bg-muted/50 px-2 py-1 rounded">
                      <span>({link.from.q},{link.from.r})→({link.to.q},{link.to.r}) cost:{link.cost}</span>
                      <button
                        className="text-destructive hover:text-destructive/80 ml-2"
                        onClick={() => setFerryLinks(prev => prev.filter(l => l.id !== link.id))}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {adjacentLinks.length > 0 && (
              <div>
                <h4 className="text-xs font-medium mb-1 text-accent-foreground">Adjacent Links ({adjacentLinks.length})</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {adjacentLinks.map((link) => (
                    <div key={link.id} className="flex items-center justify-between text-xs bg-muted/50 px-2 py-1 rounded">
                      <span>({link.from.q},{link.from.r})↔({link.to.q},{link.to.r}) cost:{link.cost}</span>
                      <button
                        className="text-destructive hover:text-destructive/80 ml-2"
                        onClick={() => setAdjacentLinks(prev => prev.filter(l => l.id !== link.id))}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* メインエリア（ヘクスグリッド） */}
      <div className="flex-1 min-w-0">
        <HexGrid
          hexes={hexes}
          selectedHex={selectedHex}
          onHexClick={handleHexClick}
          onHexRightClick={handleHexRightClick}
          orientation={orientation}
          onToggleOrientation={() => setOrientation(orientation === 'portrait' ? 'landscape' : 'portrait')}
          zoomLevel={zoomLevel}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          ferryLinks={ferryLinks}
          adjacentLinks={adjacentLinks}
          linkStartHex={linkStartHex}
          temporaryTrack={temporaryTrackPlacement}
        />
      </div>

      {/* 右サイドパネル（エクスポート） */}
      <div className="w-80 flex-shrink-0 border-l border-border overflow-y-auto p-4">
        <ExportPanel
          hexes={hexes}
          mapInfo={mapInfo}
          onMapInfoChange={setMapInfo}
        />
      </div>

      {/* 都市/町編集ダイアログ */}
      {editingHex && (
        <CityTownEditor
          mode={editingHex.mode}
          hexCoord={{ q: editingHex.hex.q, r: editingHex.hex.r }}
          existingCity={editingHex.mode === 'city' ? editingHex.hex.city : undefined}
          existingTown={editingHex.mode === 'town' ? editingHex.hex.town : undefined}
          onSave={handleSaveCityTown}
          onCancel={() => setEditingHex(null)}
          onDelete={handleDeleteCityTown}
        />
      )}
    </div>
  );
}

/**
 * EditorHex配列をマップスキーマ形式のJSONに変換
 */
function convertToMapSchema(
  hexes: EditorHex[],
  mapInfo: MapInfo,
  ferryLinks: EditorFerryLink[],
  adjacentLinks: EditorAdjacentLink[]
): object {
  const cities = hexes
    .filter((hex) => hex.city)
    .map((hex) => ({
      name: hex.city!.name,
      color: hex.city!.colors,  // 配列形式で保存
      coordinates: { q: hex.q, r: hex.r },
      dice_color: hex.city!.diceColors.length === 1 ? hex.city!.diceColors[0] : hex.city!.diceColors,
      dice_numbers: hex.city!.diceNumbers,  // 空配列も許容
    }));

  const towns = hexes
    .filter((hex) => hex.town && !hex.city)
    .map((hex) => ({
      name: hex.town!.name,
      coordinates: { q: hex.q, r: hex.r },
      urbanizable: hex.town!.urbanizable,
      terrain: hex.terrain === 'plains' ? 'plain' : hex.terrain,
    }));

  const mountains = hexes
    .filter((hex) => hex.terrain === 'mountains' && !hex.city && !hex.town)
    .map((hex) => ({ q: hex.q, r: hex.r }));

  const rivers = hexes
    .filter((hex) => hex.terrain === 'river' && !hex.city && !hex.town)
    .map((hex) => ({ q: hex.q, r: hex.r }));

  const plains = hexes
    .filter((hex) => hex.terrain === 'plains' && !hex.city && !hex.town)
    .map((hex) => ({ q: hex.q, r: hex.r }));

  // リンクデータを変換（IDを除く）
  const ferry_links = ferryLinks.map(link => ({
    from: link.from,
    to: link.to,
    cost: link.cost,
  }));

  const adjacent_links = adjacentLinks.map(link => ({
    from: link.from,
    to: link.to,
    cost: link.cost,
  }));

  // 壁を抽出
  const walls = hexes
    .filter((hex) => hex.blockedEdges && hex.blockedEdges.length > 0)
    .map((hex) => ({
      coordinates: { q: hex.q, r: hex.r },
      blocked_edges: hex.blockedEdges!,
    }));

  // 敷設済み線路を抽出
  const preLaidTracks = hexes
    .filter((hex) => hex.preLaidTrack)
    .map((hex) => ({
      coordinates: { q: hex.q, r: hex.r },
      tile_type: hex.preLaidTrack!.tileType,
      rotation: hex.preLaidTrack!.rotation,
    }));

  const result: Record<string, unknown> = {
    map_info: {
      name: mapInfo.name,
      designer: mapInfo.designer,
      players: mapInfo.players,
      coordinate_system: 'axial',
      origin: { q: 0, r: 0 },
      orientation: 'pointy_top',
    },
    cities,
    towns,
    terrain: {
      mountains,
      rivers,
      plains,
    },
  };

  // リンクがある場合のみ追加
  if (ferry_links.length > 0) {
    result.ferry_links = ferry_links;
  }
  if (adjacent_links.length > 0) {
    result.adjacent_links = adjacent_links;
  }

  // 壁がある場合のみ追加
  if (walls.length > 0) {
    result.walls = walls;
  }

  // 敷設済み線路がある場合のみ追加
  if (preLaidTracks.length > 0) {
    result.pre_laid_tracks = preLaidTracks;
  }

  return result;
}

export default MapEditor;
